import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PromiseSetsList, ServiceLinesResponse, StandingDataBookingReasonsResponse, StandingDataCancellationReasonsResponse, SiteTypes, TimeBands } from '../../../APIDocs/apiTypes';
import { RootState } from '@/store';

/**
 * DNO / BNO dropdown values
 */
export interface DnoBnoDropdowns {
  ctRatio: string[];
  serviceVoltage: string[];
  fuseSize: string[];
  mtrPointVoltage: string[];
  serviceRating: string[];
  vtRatio: string[];
}

/**
 * Full standing data response
 */
export interface StandingDataResponse {
  bookingReasons: StandingDataBookingReasonsResponse;
  cancellationReasons: StandingDataCancellationReasonsResponse;
  serviceLines: ServiceLinesResponse;
  promiseSets: PromiseSetsList;
  dnoBnoDropdowns?: DnoBnoDropdowns;
  siteTypes?: SiteTypes;
  timeBands?: TimeBands;
}

export interface StandingDataState {
  bookingReasons: StandingDataBookingReasonsResponse;
  cancellationReasons: StandingDataCancellationReasonsResponse;
  serviceLines: ServiceLinesResponse;
  promiseSets: PromiseSetsList;
  dnoBnoDropdowns: DnoBnoDropdowns | null;
  siteTypes: SiteTypes | null;
  timeBands: TimeBands | null;
  loaded: boolean;
}

const initialState: StandingDataState = {
  bookingReasons: [],
  cancellationReasons: [],
  serviceLines: [],
  promiseSets: [],
  dnoBnoDropdowns: null,
  siteTypes: null,
  timeBands: null,
  loaded: false
};

export const standingDataSlice = createSlice({
  name: 'standingData',
  initialState,
  reducers: {
    setStandingData: (state, action: PayloadAction<StandingDataResponse>) => {
      state.bookingReasons = action.payload.bookingReasons;
      state.cancellationReasons = action.payload.cancellationReasons;
      state.serviceLines = action.payload.serviceLines;
      state.promiseSets = action.payload.promiseSets;
      state.dnoBnoDropdowns = action.payload.dnoBnoDropdowns ?? null;
      state.siteTypes = action.payload.siteTypes ?? null;
      state.timeBands = action.payload.timeBands ?? null;
      state.loaded = true;
    },
    clearStandingData: () => initialState
  }
});

export const selectStandingData = (state: RootState) => state.standingData;

export const selectServiceLines = (state: RootState) => state.standingData.serviceLines;

export const selectBookingReasons = (state: RootState) => state.standingData.bookingReasons;

export const selectCancellationReasons = (state: RootState) => state.standingData.cancellationReasons;

export const selectPromiseSets = (state: RootState) => state.standingData.promiseSets;

export const selectTimeBands = (state: RootState) => state.standingData.timeBands;

export const selectSiteTypes = (state: RootState) => state.standingData.siteTypes;

export const selectDnoBnoDropdowns = (state: RootState) => state.standingData.dnoBnoDropdowns;

export const selectStandingDataLoaded = (state: RootState) => state.standingData.loaded;

export const { setStandingData, clearStandingData } = standingDataSlice.actions;

export default standingDataSlice.reducer;
